import {
  Box,
  Grid,
  CardContent,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getTooltipLabel } from '@bumble/app/utils/get-date';
import DashboardTimeRange from '@bumble/app/components/DashboardTimeRange';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useEffect, useState } from 'react';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { useCpe } from './CpeContext';
import { SecurityServicesMap } from './SecurityServicesMap';
import { Loading } from '@bumble/app/components/Loading';
import { GridColDef } from '@mui/x-data-grid';
import DataTableGrid from '@bumble/app/components/DataTableGrid';

interface NodeData {
  latitude: number;
  longitude: number;
  node_id: string;
  node_name: string;
  site_address: string;
  malicious_ips: number;
  total_ips: number;
}

const columns: GridColDef[] = [
  {
    field: 'src_addr',
    headerName: 'IP Address',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'start_time',
    headerName: 'Start Time (UTC)',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'duration',
    headerName: 'Duration (sec)',
    width: 70,
    disableColumnMenu: true,
  },
  {
    field: 'attack_throughput',
    headerName: 'Throughput (bps)',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'attack_volume',
    headerName: 'Volume (Bytes)',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'attack_name',
    headerName: 'Description',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'attack_count',
    headerName: 'Attack Counts',
    width: 100,
    disableColumnMenu: true,
  },
];

const trafficColumns: GridColDef[] = [
  {
    field: 'ip_address',
    headerName: 'Blocked IP Address',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'country',
    headerName: 'Country',
    width: 130,
    disableColumnMenu: true,
  },
  {
    field: 'city',
    headerName: 'City',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'malicious_score',
    headerName: 'Malicious Reputation Score',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'block_time',
    headerName: 'Blocked Time',
    width: 200,
    disableColumnMenu: true,
  },
]

const columnVisible = {
  src_addr: true,
  status: true,
  start_time: true,
  duration: true,
  attack_throughput: true,
  attack_volume: true,
  attack_name: true,
  attack_count: true,
};

const trafficColumnVisible = {
  ip_address: true,
  country: true,
  city: true,
  malicious_score: true,
  block_time: true,
};

interface SecurityServicesDashboardProps {
  selectedTab: string;
}

export const SecurityServicesDashboard = ({selectedTab}: SecurityServicesDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const { cpe, setCpe } = useCpe();
  const [inboundData, setInboundData] = useState<any[]>([]);
  const [inNodeData, setInNodeData] = useState<object>();
  const [outboundData, setOutboundData] = useState<any[]>([]);
  const [outNodeData, setOutNodeData] = useState<object>();
  const [blockIPData, setBlockIPData] = useState<any[]>([]);
  const [blockNodeData, setBlockNodeData] = useState<object>();
  const [trafficIPData, setTrafficIPData] = useState<any[]>([]);

  const api = useApi();
  let oldCpeName: string, oldTimeRange: DurationLikeObject;

  const apiCall1 = useMutation(api.dashboardGetInboundSrcIpApiV1DashboardGetInboundSrcIpGet, {
    onSuccess(data) {
      const inboundData = (data.data as any).inbound_src_ips
      const nodeData = (data.data as any).node;
      setInboundData(inboundData);
      setInNodeData({
        ...nodeData,
        site_address: nodeData.site_address ?? ''
      });
    },
    onError: apiError
  })

  const apiCall2 = useMutation(api.dashboardGetOutboundDstIpApiV1DashboardGetOutboundDstIpGet, {
    onSuccess(data) {
      const fullData = (data.data as any).outbound_dst_ips;
      const nodeData = (data.data as any).node;
      setOutboundData(fullData);
      setOutNodeData({...nodeData, site_address: nodeData.site_address ?? ''});
    },
    onError: apiError
  })

  const getBlockData = useMutation(api.dashboardGetBlockedIpsApiV1DashboardGetBlockedIpsGet, {
    onSuccess (data) {
      const fullData = (data.data as any).blocked_ips;
      const nodeData = (data.data as any).node;
      setBlockIPData(fullData);
      setBlockNodeData({...nodeData, site_address: nodeData.site_address ?? ''});
    },
    onError: apiError
  })

  const { isLoading, data, refetch, fetchStatus, isRefetching } = useQuery(
    ['DosStatsData'],
    api.getDosStatsApiV1GetDosStatsGet.bind(null, {
      node_id: cpe.nodeId,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false, retry: false, onError: apiError },
  );

  const {
    data: trafficData,
    isLoading: trafficDataLoading,
    fetchStatus: trafficfetchStatus,
    isRefetching: isTrafficRefetching,
    refetch: trafficRefetch
  } = useQuery(
    ['TrafficData'],
    api.dashboardGetBlockedIpsHistoryApiV1DashboardGetBlockedIpsHistoryGet.bind(null, {
      node_id: cpe.nodeId,
      node_name: cpe.cpeName,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false, retry: false, onError: apiError },
  );

  useEffect(() => {
    setTrafficIPData((trafficData?.data as any)?.blocked_ips || []);
  }, [trafficData]);

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const handleCpeChange = (val: any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id,
      });
    }
  };

  useEffect(() => {
    if (cpe.cpeName && timeRange && (cpe.cpeName !== oldCpeName || timeRange !== oldTimeRange)) {
      apiCall1.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      apiCall2.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      getBlockData.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      refetch();
      trafficRefetch();
      oldCpeName = cpe.cpeName;
      oldTimeRange = timeRange;
    }
  }, [timeRange, cpe])

  const handleRefreshClick = () => {
    if (cpe.cpeName && timeRange) {
      apiCall1.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      apiCall2.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      getBlockData.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      refetch();
      trafficRefetch();
    }
    toast.success('Refreshed chart data.');
  };

  function CustomizedTicks(props: any) {
    const { x, y, payload } = props;
    const convertedDate = typeof payload.value === 'string'
      ? new Date(payload.value).valueOf()
      : payload.value;
    const date = DateTime.fromMillis(convertedDate);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date?.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date?.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 0, py: 5 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <Grid container spacing={5}>
        <Grid item lg={6} xs={12}>
          <SecurityServicesMap
            devices={inboundData}
            nodeData = {inNodeData as NodeData}
            type='inbound'
            loading={apiCall1.isLoading}
          />
        </Grid>
        <Grid item lg={6} xs={12} sx={{ mt: { xs:20, lg: 0 } }}>
          <SecurityServicesMap
            devices={outboundData}
            nodeData = {outNodeData as NodeData}
            type='outbound'
            loading={apiCall2.isLoading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={5} mt={36}>
        <Grid item lg={6} xs={12} mt={4}>
          <SecurityServicesMap
            devices={blockIPData}
            nodeData = {blockNodeData as NodeData}
            type='blocked'
            loading={getBlockData.isLoading}
          />
        </Grid>
        <Grid item lg={6} xs={12} mt={23}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }} >
            <Typography sx={{ mr: 10, mb: 3, display: { xs: 'block', md: 'block' } }}>
              Blocked Malicious Traffic
            </Typography>
            {((trafficDataLoading || isTrafficRefetching) && trafficfetchStatus !== 'idle') ? <Loading height={370} /> :
              <Box sx={{ width: '100%', height: 375, overflow: 'auto' }}>
                <DataTableGrid
                  rows={trafficIPData.map((item, index) => ({
                    ...item,
                    id: index
                  }))}
                  columns={trafficColumns}
                  columnVisibilityModel={trafficColumnVisible}
                  slots
                />
              </Box>
            }
          </CardContent>
        </Grid>
      </Grid>
      <h2 style={{ marginTop: '40px' }}>DDoS Mitigation</h2>
      <Grid container spacing={5}>
        <Grid item lg={6} xs={12}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
            <Typography color="primary" sx={{ mb: 2 }}>DDoS Mitigation Traffic</Typography>
            {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={370} /> :
            (<ResponsiveContainer width="100%" height={400}>
              <LineChart
                margin={{ top: 5, right: 10, left: 15, bottom: 5, }}
                data={data?.data.history}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="start_time"
                  type="category"
                  allowDuplicatedCategory={false}
                  height={40}
                  interval={180}
                  tick={<CustomizedTicks />}
                />
                <YAxis
                  orientation="left"
                  type="number"
                  domain={[0, 'auto']}
                  width={30}
                  tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)}
                />
                <Line
                  strokeWidth={2}
                  stroke={'#C5904A'}
                  dot={false}
                  dataKey="attack_throughput"
                  name="Attack Throughput (bps)"
                />
                <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
              </LineChart>
            </ResponsiveContainer>)}
          </CardContent>
        </Grid>
        <Grid item lg={6} xs={12}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }} >
            <Typography sx={{ mr: 10, mb: '15px', display: { xs: 'block', md: 'block' } }}>
              DDoS Attack List
            </Typography>
            {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={370} /> :
              <Box sx={{ width: '100%', height: 395, overflow: 'auto' }}>
                <DataTableGrid
                  rows={
                    data?.data.history.map((item, index) => ({
                      ...item,
                      id: index,
                    })) || []
                  }
                  columns={columns}
                  columnVisibilityModel={columnVisible}
                  slots
                />
              </Box>
            }
          </CardContent>
        </Grid>
      </Grid>
    </Box>
  );
};
